import { Box, Typography } from '@mui/material';
import { useChartColors } from '../dashboard/charts/theme';
import { SR_ONLY } from './heroStyles';

export type ThreadStepState = 'done' | 'current' | 'todo';

export interface ThreadStep {
    key: string;
    label: string;
    state: ThreadStepState;
    /** Done after its target date, or still open past it. Shown by shape and words, not colour only. */
    late?: boolean;
    /** One short visible line under the label, e.g. "Done 12 Oct" or "Due 6 Nov · cut 22,800". */
    note?: string;
}

export interface OrderThreadProps {
    steps: ThreadStep[];
    /** One thin row of beads with no labels, for boards that list many orders. */
    compact?: boolean;
    /** What the thread belongs to, read first by screen readers ("Order BO-2417"). */
    name?: string;
    emptyText?: string;
}

const STATE_WORDS: Record<ThreadStepState, string> = { done: 'done', current: 'current step', todo: 'not started' };

/** Summary used as the compact thread's accessible name and in tests. */
export function threadSummary(steps: ThreadStep[], name?: string): string {
    const prefix = name ? `${name}: ` : '';
    if (steps.length === 0) return `${prefix}no steps planned.`;
    const lateCount = steps.filter(s => s.late).length;
    const lateText = lateCount ? `, ${lateCount} late` : ', on plan';
    const idx = steps.findIndex(s => s.state === 'current');
    if (idx >= 0) return `${prefix}at step ${idx + 1} of ${steps.length}, ${steps[idx].label}${lateText}.`;
    if (steps.every(s => s.state === 'done')) return `${prefix}all ${steps.length} steps done${lateText}.`;
    return `${prefix}not started${lateText}.`;
}

function Bead({ step, size, thread, bad, grid, surface }: {
    step: ThreadStep; size: number; thread: string; bad: string; grid: string; surface: string;
}) {
    const color = step.late ? bad : thread;
    const r = size / 2 - 2;
    const mid = size / 2;
    return (
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden focusable="false" style={{ display: 'block', flex: 'none' }}>
            {step.state === 'current' && <circle cx={mid} cy={mid} r={mid} fill={color} fillOpacity={0.18} />}
            <circle
                cx={mid} cy={mid} r={step.state === 'current' ? r - 2 : r}
                fill={step.state === 'done' ? color : surface}
                stroke={step.state === 'todo' && !step.late ? grid : color}
                strokeWidth={2}
            />
            {/* shape marks so state never rests on colour: tick = done on time, ! = late */}
            {step.late ? (
                <path d={`M${mid} ${mid - r * 0.5} V${mid + r * 0.1} M${mid} ${mid + r * 0.42} v0.01`}
                      stroke={step.state === 'done' ? surface : color} strokeWidth={2} strokeLinecap="round" />
            ) : step.state === 'done' ? (
                <path d={`M${mid - r * 0.45} ${mid} l${r * 0.3} ${r * 0.32} l${r * 0.6} -${r * 0.62}`}
                      fill="none" stroke={surface} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
            ) : null}
        </svg>
    );
}

/**
 * One buyer order's journey drawn as a sewing thread: stitched (solid) through the steps that are
 * done, dashed after. Each bead is a T&A pin. At 600px and wider it runs left to right with labels
 * under the beads; narrower it turns vertical with labels beside them, so long (Bangla) labels wrap
 * instead of overflowing. The list itself is the screen-reader twin: every step says its state.
 */
export default function OrderThread({ steps, compact = false, name, emptyText = 'No T&A steps yet.' }: OrderThreadProps) {
    const c = useChartColors();
    const thread = c.series[1];
    const bad = c.series[7];
    const summary = threadSummary(steps, name);

    if (steps.length === 0) {
        return <Typography variant="body2" color="text.secondary" data-testid="order-thread-empty">{emptyText}</Typography>;
    }

    // the thread keeps its colour; lateness sits on the beads (shape + words), so one late step doesn't paint it all red
    const runColor = thread;
    const lastDone = steps.reduce((acc, s, i) => (s.state === 'done' ? i : acc), -1);

    if (compact) {
        const at = steps.findIndex(s => s.state === 'current');
        const reach = at >= 0 ? at : lastDone;
        const runPct = steps.length > 1 && reach > 0 ? (reach / (steps.length - 1)) * 100 : 0;
        return (
            <Box data-testid="order-thread" data-compact="true" role="img" aria-label={summary}
                 sx={{ position: 'relative', display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: 18, minWidth: 0 }}>
                <Box aria-hidden sx={{ position: 'absolute', left: 6, right: 6, top: '50%', borderTop: `2px dashed ${c.grid}`, mt: '-1px' }} />
                <Box aria-hidden sx={{ position: 'absolute', left: 6, top: '50%', height: 3, mt: '-1.5px', borderRadius: 2,
                                       width: `calc((100% - 12px) * ${runPct / 100})`, bgcolor: runColor }} />
                {steps.map(s => (
                    <Box key={s.key} sx={{ position: 'relative' }}>
                        <Bead step={s} size={12} thread={thread} bad={bad} grid={c.grid} surface={c.surface} />
                    </Box>
                ))}
            </Box>
        );
    }

    const BEAD = 22;
    return (
        <Box data-testid="order-thread" data-compact="false">
            <Box component="span" sx={SR_ONLY}>{summary}</Box>
            <Box
                component="ol"
                aria-label={name ? `${name} steps` : 'Order steps'}
                sx={{
                    listStyle: 'none', m: 0, p: 0, display: 'grid', minWidth: 0,
                    gridTemplateColumns: { xs: '1fr', sm: `repeat(${steps.length}, minmax(0, 1fr))` },
                    rowGap: { xs: 1.5, sm: 0 },
                }}
            >
                {steps.map((s, i) => {
                    // the segment to the next bead is stitched once the thread has reached that bead
                    const stitched = steps[i + 1] != null && steps[i + 1].state !== 'todo';
                    const lineColor = stitched ? runColor : c.grid;
                    const lineStyle = stitched ? 'solid' : 'dashed';
                    const isLast = i === steps.length - 1;
                    return (
                        <Box
                            component="li" key={s.key} data-testid={`thread-step-${s.key}`} data-state={s.state} data-late={s.late ? 'true' : 'false'}
                            sx={{
                                position: 'relative', minWidth: 0,
                                display: 'flex', flexDirection: { xs: 'row', sm: 'column' }, alignItems: { xs: 'flex-start', sm: 'center' },
                                gap: { xs: 1.25, sm: 0.75 }, textAlign: { xs: 'left', sm: 'center' }, px: { xs: 0, sm: 0.5 },
                            }}
                        >
                            {!isLast && (
                                <Box aria-hidden sx={{
                                    position: 'absolute',
                                    // horizontal: from this bead's centre to the next one
                                    top: { xs: BEAD / 2, sm: BEAD / 2 - 1.5 }, left: { xs: BEAD / 2 - 1.5, sm: '50%' },
                                    width: { xs: 0, sm: '100%' }, height: { xs: 'calc(100% + 12px)', sm: 0 },
                                    borderTop: { xs: 'none', sm: `3px ${lineStyle} ${lineColor}` },
                                    borderLeft: { xs: `3px ${lineStyle} ${lineColor}`, sm: 'none' },
                                }} />
                            )}
                            <Box sx={{ position: 'relative', zIndex: 1, bgcolor: 'background.paper', borderRadius: '50%' }}>
                                <Bead step={s} size={BEAD} thread={thread} bad={bad} grid={c.grid} surface={c.surface} />
                            </Box>
                            <Box sx={{ minWidth: 0 }}>
                                <Typography variant="caption" component="span"
                                            sx={{ display: 'block', fontWeight: s.state === 'current' ? 700 : 600, lineHeight: 1.25, overflowWrap: 'anywhere' }}>
                                    {s.label}
                                </Typography>
                                {s.note && (
                                    <Typography variant="caption" component="span" color={s.late ? 'error.main' : 'text.secondary'}
                                                sx={{ display: 'block', lineHeight: 1.25, fontVariantNumeric: 'tabular-nums', overflowWrap: 'anywhere' }}>
                                        {s.note}
                                    </Typography>
                                )}
                                <Box component="span" sx={SR_ONLY}>{`, ${STATE_WORDS[s.state]}${s.late ? ', late' : ''}`}</Box>
                            </Box>
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
}
